/**
 * Where a meeting sits on the grid.
 *
 * Kept apart from the component because this is the part that can be wrong in a way
 * nobody notices: a block half an hour off, or two overlapping meetings drawn on top of
 * each other so the one underneath is invisible. It is pure arithmetic over instants,
 * so it can be tested without a browser.
 *
 * Everything here works in the viewer's own timezone, because that is what the grid
 * shows. `Date` already does that (a Date is an instant, and `getHours()` answers in
 * local time), so there is no conversion step to get wrong.
 */

import type { Meeting } from '@/api/schema';

/** The hours a working day is drawn between. */
export const DAY_START_HOUR = 7;
export const DAY_END_HOUR = 21;

const MINUTES_IN_VIEW = (DAY_END_HOUR - DAY_START_HOUR) * 60;

/** Short enough that a 15-minute call is still readable. */
const MIN_BLOCK_MINUTES = 20;

export type PositionedMeeting = {
  meeting: Meeting;
  /** Percentages down the day column, so the grid can be any height. */
  top: number;
  height: number;
  /** Which of `columns` lanes this block occupies when meetings overlap. */
  column: number;
  columns: number;
};

/** Midnight local time on the day this instant falls in. */
export function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

/**
 * The Monday of the week containing this date.
 *
 * Monday because the office works Monday to Friday and a week starting on Sunday puts
 * the weekend at both ends. `setDate` handles month and year boundaries on its own,
 * which is the case the plan asks about: 1 March minus a few days is February, and
 * January minus a few is last year.
 */
export function startOfWeek(date: Date): Date {
  const day = startOfDay(date);
  // getDay() is 0 for Sunday, so Sunday counts as the last day of the previous week.
  const shift = (day.getDay() + 6) % 7;
  day.setDate(day.getDate() - shift);
  return day;
}

export function addDays(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
}

/** The seven days of the week containing this date. */
export function weekDays(date: Date): Date[] {
  const monday = startOfWeek(date);
  return Array.from({ length: 7 }, (_, index) => addDays(monday, index));
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Minutes from the top of the drawn day. Negative above it, over MINUTES_IN_VIEW below. */
function offsetMinutes(at: Date): number {
  return (at.getHours() - DAY_START_HOUR) * 60 + at.getMinutes();
}

/** Where the current-time line goes, or null when now is outside the drawn hours. */
export function nowOffset(now: Date): number | null {
  const minutes = offsetMinutes(now);
  if (minutes < 0 || minutes > MINUTES_IN_VIEW) return null;
  return (minutes / MINUTES_IN_VIEW) * 100;
}

export function meetingsOn(meetings: Meeting[], day: Date): Meeting[] {
  return meetings.filter((meeting) => isSameDay(new Date(meeting.scheduled_at), day));
}

/**
 * Lay out one day's meetings, side by side where they clash.
 *
 * Overlapping blocks are grouped and each group is split into lanes: a meeting takes
 * the first lane whose last block has already finished. Drawn full width they would
 * cover each other and the one underneath would simply not exist as far as the reader
 * is concerned, which is the difference between a busy day and a lost appointment.
 *
 * The lane count is per *group*, not per day, so one clash at 9am does not make every
 * meeting that afternoon half-width.
 */
export function layOutDay(meetings: Meeting[], day: Date): PositionedMeeting[] {
  const ordered = meetingsOn(meetings, day)
    .map((meeting) => {
      const start = new Date(meeting.scheduled_at);
      const startMinutes = offsetMinutes(start);
      const shown = Math.max(meeting.duration_minutes, MIN_BLOCK_MINUTES);
      return { meeting, startMinutes, endMinutes: startMinutes + shown };
    })
    .sort((a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes);

  const positioned: PositionedMeeting[] = [];
  let group: typeof ordered = [];
  let groupEnd = -Infinity;

  const flush = () => {
    if (group.length === 0) return;

    // Lanes, each holding the end time of whatever was put in it last.
    const laneEnds: number[] = [];
    const lanes = group.map((item) => {
      const lane = laneEnds.findIndex((end) => end <= item.startMinutes);
      const index = lane === -1 ? laneEnds.length : lane;
      laneEnds[index] = item.endMinutes;
      return index;
    });
    const width = laneEnds.length;

    group.forEach((item, index) => {
      // Clamped so a meeting starting before the drawn day, or running past its end,
      // still appears rather than being positioned off the grid entirely.
      const top = Math.max(0, Math.min(item.startMinutes, MINUTES_IN_VIEW));
      const bottom = Math.max(0, Math.min(item.endMinutes, MINUTES_IN_VIEW));
      positioned.push({
        meeting: item.meeting,
        top: (top / MINUTES_IN_VIEW) * 100,
        height: Math.max(((bottom - top) / MINUTES_IN_VIEW) * 100, 1),
        column: lanes[index] ?? 0,
        columns: width,
      });
    });
    group = [];
    groupEnd = -Infinity;
  };

  for (const item of ordered) {
    // A new group starts as soon as a meeting begins after everything so far has ended.
    if (item.startMinutes >= groupEnd) flush();
    group.push(item);
    groupEnd = Math.max(groupEnd, item.endMinutes);
  }
  flush();

  return positioned;
}

/** The hour labels down the side of the grid. */
export function hourLabels(): number[] {
  return Array.from({ length: DAY_END_HOUR - DAY_START_HOUR }, (_, i) => DAY_START_HOUR + i);
}
